// @flow
import React, { useEffect, useState } from 'react'
import { Subject } from 'rxjs'
import { xorBy } from 'lodash'
import Sortable from 'sortablejs'
import { insertValueInList } from './cp-draggable.helper'


type Props = {
  draggableList: Object[], // React state list that needs to be manipulated
  eventSubject: Object, // Rxjs Subject to listen to events
  dragRef: Object, // required to execute useDraggableList on
}

export const useDraggableList = (props: Props) => {
  const subject = new Subject()
  const [list, setList] = useState(props.draggableList)
  const [sortablejs, setSortable] = useState(null)
  const [eventSubject, setEventSubject] = useState(new Subject())

  useEffect(() => {
    const diff = xorBy(list, props.draggableList, 'id')
    const newList = diff.length === (props.draggableList.length + list.length)

    if(diff.length > 0){
      if(props.draggableList.length < list.length && !newList){
        // deleted
        setList(l => l.filter(a => a.id !== diff[0].id))
      }
      else if(props.draggableList.length > list.length && !newList){
        // added
        setList(l => l.concat(diff))
      }
      else if(newList){
        setList(props.draggableList)
      }
    }
    else if(list.length !== 0){
      // No diff... items and their properties have been modified
      const updatedDraggableList = []
      let listChanged = false;
      for(let i = 0; i < list.length; i++){
        for(let j = 0; j < props.draggableList.length; j++){
          if(list[i].id === props.draggableList[j].id){
            updatedDraggableList.push(props.draggableList[j])
          }
          if(list[i].id === props.draggableList[j].id && list[i] !== props.draggableList[j]){
            listChanged = true
          }
        }
      }
      if(listChanged){
        setList(updatedDraggableList)
      }
    }
  }, [props.draggableList, list])

  useEffect(() => {
    function onEnd (e) {
      Sortable.dragged.style.opacity = 1;
      setList(l => insertValueInList(l, e.oldIndex, e.newIndex))
      eventSubject.next({event: e, action: 'ON_END'})
    }

    function onStart(e) {
      Sortable.ghost.style.opacity = 1;
      Sortable.dragged.style.opacity = 0;
      eventSubject.next({event: e, action: 'ON_START'})
    }

    function onChoose(e) {
      eventSubject.next({event: e, action: 'ON_CHOOSE'})
    }

    function onClone(e) {
      eventSubject.next({event: e, action: 'ON_CLONE'})
    }


    if(props.dragRef && props.dragRef.current && !sortablejs && props.userCanEdit){
      setSortable(new Sortable(props.dragRef.current, {
        forceFallback: true,
        animation: 150,
        easing: "cubic-bezier(1, 0, 0, 1)",
        ghostClass: "Ghost",
        dragClass: "Dragging",
        fallbackClass: "Fallback",
        chosenClass: "Chosen",
        onEnd,
        onStart,
        onChoose,
        onClone,
        ...props
      }))
    }
    else if(!props.dragRef || !props.dragRef.current){
      console.warn(`Property 'dragRef' missing. 'dragRef' is used to indicate what element to enable drag and drop.`)
    }
  }, [list, props.dragRef, props.userCanEdit])

  return [list, eventSubject, Sortable]
}
